import { panoramaApi } from '@webdev/upload-management';
import { sortBy } from 'lodash-es';
import { WaypointOptions } from '../gallery/TourModeModal';
import {
  getFolder,
  getGalleryContent,
  getRootFolder,
  sanitizePanoramaDetails,
} from './UploadManagementService';
import { Gallery, ROOT_FOLDER_ID } from './UploadManagementServiceTypes';

const getAuthenticatedGalleryApi = () =>
  new panoramaApi.PanoramaTourApi(
    new panoramaApi.Configuration({
      basePath: '/api/um/account-panorama',
      apiKey: `Bearer ${JSON.parse(localStorage['kl.jwtToken'])}`,
    }),
  );
const getGalleryApi = () =>
  new panoramaApi.PanoramaTourApi(
    new panoramaApi.Configuration({ basePath: '/api/um/account-panorama', apiKey: undefined }),
  );

export interface CreateGalleryOptions {
  name: string;
  parentGuid: string | null;
  referencePanoramas: { guid: string; index: number }[];
  tourMode?: WaypointOptions;
}

export interface UpdateGalleryOptions {
  guid: string;
  referencePanoramas: { guid: string; index: number }[];
  tourMode: WaypointOptions;
}

export type TourModeSetting = 'NoMarker' | 'TwoMarker' | 'AllMarkers';

export class TourModeConverter {
  private static synonyms: Array<[WaypointOptions, TourModeSetting, panoramaApi.TourMode]> = [
    ['none', 'NoMarker', 0],
    ['2', 'TwoMarker', 1],
    ['all', 'AllMarkers', 2],
  ];
  static toAPI(mode?: WaypointOptions): panoramaApi.TourMode {
    const synonym = this.synonyms.find(([a]) => mode === a);
    return synonym?.[2] || 0;
  }
  static fromAPI(mode?: TourModeSetting): WaypointOptions {
    const synonym = this.synonyms.find(([, b]) => mode === b);
    return synonym?.[0] || 'none';
  }
}

export const sanitizeGallery = (rawGallery: any): Gallery => ({
  name: rawGallery.name,
  guid: rawGallery.panoramaTourGuid,
  panoramas: sortBy(rawGallery.tourPanoramas, ['index']).map((entry) =>
    sanitizePanoramaDetails(entry.panoramaMetaData),
  ),
  tourMode: TourModeConverter.fromAPI(rawGallery.tourMode),
});

export const getGalleryParentFolder = async (guid: string) => {
  const galleryContent = await getGalleryContent(guid);
  if (!galleryContent.parentGuid) {
    return getRootFolder();
  }
  return getFolder(galleryContent.parentGuid);
};

export const createGallery = (args: CreateGalleryOptions) =>
  getAuthenticatedGalleryApi()
    .panoramaTourPost({
      createPanoramaTourRequest: {
        ...args,
        parentGuid: args.parentGuid === ROOT_FOLDER_ID ? null : args.parentGuid,
        tourMode: TourModeConverter.toAPI(args.tourMode),
      },
    })
    .then(sanitizeGallery);

export const updateGallery = ({ guid, referencePanoramas, tourMode }: UpdateGalleryOptions) =>
  getAuthenticatedGalleryApi()
    .panoramaTourGuidPatch({
      guid,
      patchPanoramaTourRequest: {
        referencePanoramas,
        tourMode: TourModeConverter.toAPI(tourMode),
      },
    })
    .then(sanitizeGallery);

export const getGallery = (requestOptions: panoramaApi.PanoramaTourGuidGetRequest) =>
  getAuthenticatedGalleryApi().panoramaTourGuidGet(requestOptions).then(sanitizeGallery);

export const getSharedGallery = (
  requestOptions: panoramaApi.PanoramaTourSharedShareGuidContentGuidGetRequest,
) =>
  getGalleryApi().panoramaTourSharedShareGuidContentGuidGet(requestOptions).then(sanitizeGallery);
